import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { EditorProps as CoreEditorProps, SelectionState } from 'draft-js';
import {
  CompositeDecorator,
  ContentState,
  EditorState,
  convertFromRaw,
} from 'draft-js';

import classnames from 'clsx';
import CoreEditor from 'draft-js-plugins-editor';

import type PluginEditor from 'draft-js-plugins-editor/lib';

import { formatClassNames } from '@wix/editor-elements-common-utils';
import {
  editorStateToHtml,
  htmlToEditorState,
  isMaxLength,
} from '../convertors';
import Toolbar from '../toolbars/Toolbar';
import { textAlignmentStylesFn } from '../toolbars/buttons/TextAlignButtons/common';
import { textSizeStylesFn } from '../toolbars/buttons/TextSizeButton/utils/utils';
import StylesContext from '../StylesContext';
import { TestIds } from '../../constants';
import type {
  IRichTextBoxImperativeActions,
  IRichTextBoxMapperProps,
  IRichTextBoxProps,
} from '../../RichTextBox.types';
import semanticClassNames from '../../RichTextBox.semanticClassNames';
import preventWixFocusRingAccessibility from './preventWixFocusRingAccessibility';
import { linkDecorator } from './decorators/linkDecorator';
import '../.global.scss';
import editorStyles from './editorStyles.scss';
import { useCommands } from './commands';

const decorator = new CompositeDecorator([linkDecorator]);

export type ToolbarType = {
  toolbarPosition: IRichTextBoxProps['toolbarPosition'];
  instance: any;
  plugin: any;
};

export type EditorProps = Partial<CoreEditorProps> &
  Pick<
    IRichTextBoxProps,
    | 'value'
    | 'maxLength'
    | 'allowLinks'
    | 'componentViewMode'
    | 'hideValidityIndication'
    | 'onValueChange'
    | 'validateValueAndShowIndication'
    | 'openMobileLinkModalTemp'
    | 'closeMobileLinkModalTemp'
    | 'linkModalId'
    | 'label'
    | 'scopedClassName'
    | 'translations'
    | 'shouldShowTooltips'
  > & {
    toolbar: ToolbarType;
    onChangeState: IRichTextBoxProps['onChange'];
    rootId: IRichTextBoxProps['id'];
    direction: IRichTextBoxMapperProps['direction'];
    labelDirection: IRichTextBoxMapperProps['labelDirection'];
  };

const emptyContentState = convertFromRaw({
  entityMap: {},
  blocks: [
    {
      text: '',
      key: 'initializing',
      type: 'unstyled',
      entityRanges: [],
      depth: 0,
      inlineStyleRanges: [],
    },
  ],
});

const Editor: React.ForwardRefRenderFunction<
  IRichTextBoxImperativeActions,
  EditorProps
> = (
  {
    value,
    maxLength,
    toolbar,
    readOnly,
    placeholder,
    textAlignment,
    componentViewMode,
    allowLinks,
    onBlur = () => {},
    onChangeState = () => {},
    onFocus = () => {},
    onValueChange = () => {},
    hideValidityIndication = () => {},
    validateValueAndShowIndication = () => {},
    openMobileLinkModalTemp,
    closeMobileLinkModalTemp,
    rootId,
    linkModalId,
    label,
    scopedClassName,
    labelDirection,
    direction,
    translations,
    shouldShowTooltips,
  },
  ref,
) => {
  const [editorState, setEditorState] = useState<EditorState>(
    EditorState.createWithContent(emptyContentState),
  );

  const [changed, setChanged] = useState(false);
  const [overrideDialogIsOpen, setOverrideDialogIsOpen] = useState(false);
  const [onChangeTriggered, setOnChangeTriggered] = useState(false);
  const stylesContext = useContext(StylesContext);
  const [
    lastSelectionBeforeKeyboardNavigation,
    setLastSelectionBeforeKeyboardNavigation,
  ] = useState<SelectionState>();

  const selection = useMemo(() => editorState.getSelection(), [editorState]);

  useEffect(() => {
    if (!onChangeTriggered) {
      const [oldValue, newValue] = [editorStateToHtml(editorState), value].map(
        v =>
          editorStateToHtml(
            EditorState.set(htmlToEditorState(v), { decorator }),
          ).replace(/\u00a0/g, ' '),
      );
      if (oldValue !== newValue) {
        const newState = htmlToEditorState(value, maxLength);
        const newStateDecorated = EditorState.set(newState, { decorator });
        setEditorState(newStateDecorated);
      }
    }
    setOnChangeTriggered(false);
    // eslint-disable-next-line
  }, [value, maxLength]);

  useEffect(() => {
    preventWixFocusRingAccessibility();
  }, []);

  const editorRef = useRef<PluginEditor>(null);

  const getStateWithMaxLengthEnsured = useCallback(
    (
      newState: Draft.EditorState,
      oldState: Draft.EditorState,
    ): Draft.EditorState => {
      if (maxLength !== 0 && !maxLength) {
        return newState;
      }
      const contentState = newState.getCurrentContent();
      const oldContent = oldState.getCurrentContent();

      if (contentState === oldContent || !isMaxLength(newState, maxLength)) {
        return newState;
      }
      return EditorState.undo(
        EditorState.push(
          oldState,
          ContentState.createFromText(oldContent.getPlainText()),
          'delete-character',
        ),
      );
    },
    [maxLength],
  );

  const onChange = useCallback(
    (state: EditorState, oldState: EditorState) => {
      const newState = EditorState.set(state, { decorator });
      const validatedNewState = getStateWithMaxLengthEnsured(
        newState,
        oldState,
      );
      const newHtmlState = editorStateToHtml(validatedNewState);
      const newHtmlStateValue = newHtmlState.replace(/\u00a0/g, ' ');
      const oldHtmlStateValue = editorStateToHtml(oldState).replace(
        /\u00a0/g,
        ' ',
      );
      setEditorState(validatedNewState);
      if (newHtmlStateValue !== oldHtmlStateValue) {
        setOnChangeTriggered(true);
        onValueChange(newHtmlState);
        setChanged(true);
        hideValidityIndication();
      }
    },
    [onValueChange, hideValidityIndication, getStateWithMaxLengthEnsured],
  );
  const _onBlur: React.FocusEventHandler<HTMLInputElement> = event => {
    onBlur(event);
    if (changed) {
      onChangeState(event);
      validateValueAndShowIndication({ value: event.target.textContent || '' });
      setChanged(false);
    }
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = event => {
    if (event.key === 'Tab') {
      setLastSelectionBeforeKeyboardNavigation(selection);
    }
  };

  const focus = () => {
    editorRef!.current!.focus();
  };

  React.useImperativeHandle(ref, () => {
    return {
      focus,
      blur: () => {
        editorRef!.current!.blur();
      },
      // TODO - Currently we don't show a message, even in Bolt.
      setCustomValidity: _message => {},
    };
  });

  const { commandsBindingFn, handleKeyCommand, linkModalButtonRef } =
    useCommands(editorState, onChange);

  const { toolbarPosition, instance: Instance, plugin } = toolbar;

  const onLabelClick = () => {
    editorRef.current?.focus();
  };

  return (
    <>
      {label && (
        <label
          dir={labelDirection ?? 'inherit'}
          id={`input_${rootId}`}
          onClick={onLabelClick}
          className={classnames(editorStyles.label, {
            [editorStyles.requiredIndication]: stylesContext.required,
          })}
          data-testid={TestIds.label}
        >
          {label}
        </label>
      )}
      <div
        className={classnames(
          editorStyles.editor,
          formatClassNames(semanticClassNames.input),
          {
            [editorStyles.hovered]: stylesContext.hovered,
            [editorStyles.focused]: stylesContext.focused,
            [editorStyles.error]: stylesContext.error,
            [editorStyles.disabled]: stylesContext.disabled,
            [editorStyles.useTopToolbar]: toolbarPosition === 'top',
            [editorStyles.useBottomToolbar]: toolbarPosition === 'bottom',
            [editorStyles.useInlineToolbar]: toolbarPosition === 'inline',
            [editorStyles.overrideDialogIsOpen]: overrideDialogIsOpen,
            [editorStyles.withLabel]: !!label,
          },
        )}
        onMouseUp={focus}
        onClick={focus}
        onKeyDown={onKeyDown}
        onBlur={_onBlur}
        onFocus={onFocus}
      >
        <CoreEditor
          blockStyleFn={contentBlock => {
            const textAlignmentClass = textAlignmentStylesFn(
              contentBlock,
              textAlignment,
            );
            const textSizeClass = textSizeStylesFn(contentBlock);
            return classnames(textAlignmentClass, textSizeClass);
          }}
          editorState={editorState}
          onChange={newState => {
            onChange(newState, editorState);
          }}
          plugins={[plugin]}
          ref={editorRef}
          handleKeyCommand={handleKeyCommand}
          keyBindingFn={commandsBindingFn}
          readOnly={readOnly}
          placeholder={placeholder}
          {...(label ? { tabIndex: 0, ariaLabelledBy: `input_${rootId}` } : {})}
          {...(componentViewMode === 'editor' ? { textAlignment } : {})}
        />
        <Toolbar
          direction={direction}
          lastSelectionBeforeKeyboardNavigation={
            lastSelectionBeforeKeyboardNavigation
          }
          ToolbarInstance={Instance}
          type={toolbarPosition}
          allowLinks={allowLinks}
          linkModalId={linkModalId}
          openMobileLinkModalTemp={openMobileLinkModalTemp}
          closeMobileLinkModalTemp={closeMobileLinkModalTemp}
          onOverrideDialogOpen={() => setOverrideDialogIsOpen(true)}
          onOverrideDialogClose={() => setOverrideDialogIsOpen(false)}
          scopedClassName={scopedClassName}
          linkModalButtonRef={linkModalButtonRef}
          rootId={rootId}
          translations={translations}
          shouldShowTooltips={shouldShowTooltips}
        />
      </div>
    </>
  );
};

export default React.forwardRef(Editor);
